import React, {useMemo, useRef, useState, useEffect, useCallback} from 'react'
import {Box, Link as ChakraLink, IconButton} from '@chakra-ui/react'
import PropTypes from 'prop-types'
import {Link as RouterLink} from 'react-router-dom'
import {buildCdnImageUrl, canOptimizeImage} from '../../../../utils/image-cdn-utils'

/* Context */
import {useGetLinkData} from '../../../../contexts/category-url-context'

/* Icons */
import {Play, Pause} from '../../../icons/index'

/**
 * Default breakpoint configuration for responsive images (hero/full-width)
 * Using media queries ensures correct image loads regardless of device pixel ratio
 */
const DEFAULT_BREAKPOINTS = {
    mobile: {maxWidth: 767, imageWidth: 768, retinaWidth: 1536}, // 2x for retina
    tablet: {minWidth: 768, maxWidth: 1023, imageWidth: 1024, retinaWidth: 2048},
    desktop: {minWidth: 1024, imageWidth: 1920, retinaWidth: 3840}
}

/**
 * Calculate breakpoints with custom image widths
 * @param {Array<number>} widths - Array of [mobileWidth, tabletWidth, desktopWidth]
 * @returns {Object} Breakpoint configuration with custom widths
 */
const getBreakpointsWithWidths = (widths) => {
    if (!widths || widths.length < 3) {
        return DEFAULT_BREAKPOINTS
    }
    const [mobileWidth, tabletWidth, desktopWidth] = widths
    return {
        mobile: {maxWidth: 767, imageWidth: mobileWidth, retinaWidth: mobileWidth * 2},
        tablet: {
            minWidth: 768,
            maxWidth: 1023,
            imageWidth: tabletWidth,
            retinaWidth: tabletWidth * 2
        },
        desktop: {minWidth: 1024, imageWidth: desktopWidth, retinaWidth: desktopWidth * 2}
    }
}

/**
 * Check if the media type is a video based on __typename or URL extension
 */
const isVideoMedia = (media) => {
    const typename = media?.Expanded?.__typename || media?.__typename
    if (typename === 'VideoFile') return true

    const url = media?.Expanded?.Url || media?.Url
    if (url && /\.(mp4|webm|ogg|mov)(\?|$)/i.test(url)) return true

    return false
}

const ResponsiveMediaBlock = ({
    DesktopMedia,
    TabletMedia,
    MobileMedia,
    AltText,
    customMediaStyling,
    customMediaContainerStyling,
    customContainerStyling,
    customWrapperStyling,
    Link,
    asLink = true,
    cdnOptions = {},
    // Performance optimization props for LCP images
    isHero = false,
    fetchPriority = null,
    // Custom image widths for smaller display contexts (e.g., carousel tiles)
    // Array of [mobileWidth, tabletWidth, desktopWidth] in pixels
    widths = null
}) => {
    // Use custom widths if provided, otherwise use defaults
    const BREAKPOINTS = getBreakpointsWithWidths(widths)

    // Check if any media is a video
    const hasVideo =
        isVideoMedia(MobileMedia) || isVideoMedia(TabletMedia) || isVideoMedia(DesktopMedia)

    // Get image URLs from each media type
    // CMS can provide URL in two formats: either directly at Media.Url or nested at Media.Expanded.Url
    const mobileUrl = MobileMedia?.Expanded?.Url || MobileMedia?.Url
    const tabletUrl = TabletMedia?.Expanded?.Url || TabletMedia?.Url || mobileUrl
    const desktopUrl = DesktopMedia?.Expanded?.Url || DesktopMedia?.Url || tabletUrl || mobileUrl

    // Use the best available alt text
    const altText =
        AltText ||
        DesktopMedia?.Expanded?.AltText ||
        DesktopMedia?.AltText ||
        TabletMedia?.Expanded?.AltText ||
        TabletMedia?.AltText ||
        MobileMedia?.Expanded?.AltText ||
        MobileMedia?.AltText ||
        'Image'

    let linkData = null

    if (Link && asLink) {
        const getLinkData = useGetLinkData()
        linkData = useMemo(() => {
            return getLinkData({
                __typename: Link?.Expanded?.__typename,
                CategoryId: Link?.Expanded?.CategoryId,
                resolvedUrl: Link?.Expanded?.resolvedUrl
            })
        }, [Link?.Expanded?.__typename, Link?.Expanded?.CategoryId, Link?.Expanded?.resolvedUrl])
    }

    const isExternalUrl = linkData?.isExternalUrl || false
    const linkComponent = isExternalUrl ? ChakraLink : RouterLink

    // Build optimized URL for a specific width
    const getOptimizedUrl = (url, width) => {
        if (!url) return ''
        return canOptimizeImage(url)
            ? buildCdnImageUrl(url, {
                  width,
                  quality: 85,
                  format: 'auto',
                  ...cdnOptions
              })
            : url
    }

    // Build srcSet with 1x and 2x versions for retina support
    const buildSrcSet = (url, normalWidth, retinaWidth) => {
        if (!url) return ''
        const normal = getOptimizedUrl(url, normalWidth)
        const retina = getOptimizedUrl(url, retinaWidth)
        return `${normal} 1x, ${retina} 2x`
    }

    // Video state management
    const videoRef = useRef(null)
    const [isPlaying, setIsPlaying] = useState(true)

    // Get current video URL based on screen size
    const getCurrentVideoUrl = useCallback(() => {
        if (typeof window === 'undefined') return desktopUrl
        const width = window.innerWidth
        if (width < 768) return mobileUrl
        if (width < 1024) return tabletUrl
        return desktopUrl
    }, [mobileUrl, tabletUrl, desktopUrl])


    // Handle responsive video source switching
    useEffect(() => {
        if (!hasVideo || typeof window === 'undefined') return

        const video = videoRef.current
        if (!video) return

        const handleResize = () => {
            const newUrl = getCurrentVideoUrl()
            if (video.src !== newUrl) {
                const wasPlaying = !video.paused
                video.src = newUrl
                video.load()
                if (wasPlaying) {
                    video.play().catch(() => {})
                }
            }
        }

        window.addEventListener('resize', handleResize)
        return () => window.removeEventListener('resize', handleResize)
    }, [hasVideo, getCurrentVideoUrl])

    // Toggle play/pause
    const togglePlayPause = useCallback((e) => {
        e.preventDefault()
        e.stopPropagation()
        const video = videoRef.current
        if (video) {
            if (isPlaying) {
                video.pause()
                setIsPlaying(false)
            } else {
                video.play().catch(() => {})
                setIsPlaying(true)
            }
        }
    }, [isPlaying])

    // Don't render if no media available
    if (!mobileUrl && !tabletUrl && !desktopUrl) {
        return null
    }

    // Fallback src uses mobile image (smallest)
    const fallbackSrc = getOptimizedUrl(
        mobileUrl || tabletUrl || desktopUrl,
        BREAKPOINTS.mobile.imageWidth
    )

    // Handle video ready - try to play immediately
    const handleCanPlay = useCallback(() => {
        const video = videoRef.current
        if (video) {
            video.play().catch(() => {})
            setIsPlaying(true)
        }
    }, [])

    // Responsive video element for video media
    const videoElement = (
        <Box
            position="relative"
            width="100%"
            height="100%"
            {...customMediaContainerStyling}
            {...customWrapperStyling}
        >
            <video
                ref={videoRef}
                src={getCurrentVideoUrl()}
                muted
                autoPlay
                loop
                playsInline
                preload="auto"
                webkit-playsinline="true"
                x-webkit-airplay="deny"
                onCanPlay={handleCanPlay}
                onLoadedData={handleCanPlay}
                style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    ...customMediaStyling
                }}
            />
            <IconButton
                position="absolute"
                top="24px"
                right="24px"
                zIndex={10}
                variant="unstyled"
                w="32px"
                h="32px"
                minW="32px"
                onClick={togglePlayPause}
                icon={
                    isPlaying ? (
                        <Pause boxSize="32px" color="white" />
                    ) : (
                        <Play boxSize="32px" color="white" />
                    )
                }
                aria-label={isPlaying ? 'Pause' : 'Play'}
                bg="transparent"
                _hover={{bg: 'transparent'}}
            />
        </Box>
    )

    // Use <picture> element for art direction - ensures correct image per breakpoint
    const imageElement = (
        <Box {...customMediaContainerStyling} {...customWrapperStyling}>
            <picture>
                {/* Desktop: min-width 1024px */}
                {desktopUrl && (
                    <source
                        media={`(min-width: ${BREAKPOINTS.desktop.minWidth}px)`}
                        srcSet={buildSrcSet(
                            desktopUrl,
                            BREAKPOINTS.desktop.imageWidth,
                            BREAKPOINTS.desktop.retinaWidth
                        )}
                    />
                )}
                {/* Tablet: 768px - 1023px */}
                {tabletUrl && (
                    <source
                        media={`(min-width: ${BREAKPOINTS.tablet.minWidth}px) and (max-width: ${BREAKPOINTS.tablet.maxWidth}px)`}
                        srcSet={buildSrcSet(
                            tabletUrl,
                            BREAKPOINTS.tablet.imageWidth,
                            BREAKPOINTS.tablet.retinaWidth
                        )}
                    />
                )}
                {/* Mobile: up to 767px */}
                {mobileUrl && (
                    <source
                        media={`(max-width: ${BREAKPOINTS.mobile.maxWidth}px)`}
                        srcSet={buildSrcSet(
                            mobileUrl,
                            BREAKPOINTS.mobile.imageWidth,
                            BREAKPOINTS.mobile.retinaWidth
                        )}
                    />
                )}
                {/* Fallback img tag */}
                <img
                    src={fallbackSrc}
                    alt={altText}
                    loading={isHero ? 'eager' : 'lazy'}
                    fetchpriority={fetchPriority || (isHero ? 'high' : undefined)}
                    style={{
                        width: '100%',
                        height: isHero ? '100%' : 'auto',
                        objectFit: isHero ? 'cover' : undefined,
                        ...customMediaStyling
                    }}
                />
            </picture>
        </Box>
    )

    return (
        <Box
            as={Link && asLink && linkData?.generatedURL ? linkComponent : 'div'}
            {...(Link && asLink && linkData?.generatedURL && isExternalUrl ? {href: linkData?.generatedURL} : {})}
            {...(Link && asLink && linkData?.generatedURL && !isExternalUrl ? {to: linkData?.generatedURL} : {})}
            {...(Link && asLink && linkData?.generatedURL && !linkData?.stayOnSamePage && {
                target: linkData?.target,
                rel: linkData?.rel
            })}
            {...customContainerStyling}
            data-block-type="ResponsiveMediaBlock"
        >
            {hasVideo ? videoElement : imageElement}
        </Box>
    )
}

ResponsiveMediaBlock.propTypes = {
    DesktopMedia: PropTypes.object,
    TabletMedia: PropTypes.object,
    MobileMedia: PropTypes.object,
    AltText: PropTypes.string,
    customMediaStyling: PropTypes.object,
    customMediaContainerStyling: PropTypes.object,
    customContainerStyling: PropTypes.object,
    customWrapperStyling: PropTypes.object,
    Link: PropTypes.object,
    asLink: PropTypes.bool,
    cdnOptions: PropTypes.object,
    isHero: PropTypes.bool,
    fetchPriority: PropTypes.string,
    widths: PropTypes.arrayOf(PropTypes.number)
}

export default ResponsiveMediaBlock
